import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { UploadCloud, Loader2, FileSpreadsheet, ShieldCheck, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export const ARAudit = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please attach your AR aging file (.xlsx or .csv)");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      if (form.company) fd.append("company", form.company);
      fd.append("file", file);
      const { data } = await api.post("/ar-audit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success("Your free AR audit is ready");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Audit failed");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white rounded-xl border border-[#D8E6F1] px-4 py-3.5 text-[#0C2A40] placeholder:text-[#93A5B5] focus:border-[#2880B8] focus:ring-2 focus:ring-[#2880B8]/20 focus:outline-none transition-all text-sm";

  const buckets = result ? [
    { l: "0–30 days", v: result.bucket_0_30 },
    { l: "31–60 days", v: result.bucket_31_60 },
    { l: "61–90 days", v: result.bucket_61_90 },
    { l: "90+ days", v: result.bucket_90_plus, danger: true },
  ] : [];
  const maxB = result ? Math.max(...buckets.map((b) => b.v), 1) : 1;

  return (
    <section id="ar-audit" data-testid="ar-audit" className="relative z-10 bg-[#F2F8FC] py-16 md:py-24 border-t border-[#D8E6F1]">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-16 items-start">
        <div>
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#2880B8] mb-5">/ Free AR Audit</p>
          <h2 className="font-display text-4xl md:text-6xl font-medium tracking-tighter text-[#0C2A40] leading-[0.9]">
            See what your AR is <span className="font-extrabold text-[#2880B8]">really worth.</span>
          </h2>
          <p className="mt-6 text-[#51667A] text-lg leading-relaxed max-w-md">
            Drop in an export of your AR aging (Excel or CSV) and get an instant, no-cost breakdown of your aging buckets and estimated recoverable revenue.
          </p>
          <div className="mt-8 flex items-center gap-2 text-[#51667A] font-mono text-[11px] tracking-[0.12em] uppercase">
            <ShieldCheck size={16} className="text-[#2880B8]" /> Exclude PHI — we only read balances &amp; aging
          </div>
        </div>

        {!result ? (
          <motion.form
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            onSubmit={submit} className="bg-white rounded-2xl border border-[#D8E6F1] p-6 md:p-8 space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input data-testid="audit-name" required placeholder="Full name" value={form.name} onChange={update("name")} className={inputCls} />
              <input data-testid="audit-email" required type="email" placeholder="Work email" value={form.email} onChange={update("email")} className={inputCls} />
            </div>
            <input data-testid="audit-company" placeholder="Practice / company" value={form.company} onChange={update("company")} className={inputCls} />

            <button type="button" onClick={() => inputRef.current?.click()} className="w-full border-2 border-dashed border-[#D8E6F1] rounded-xl py-8 flex flex-col items-center gap-2 hover:border-[#2880B8] hover:bg-[#F2F8FC] transition-colors">
              <UploadCloud size={26} className="text-[#2880B8]" />
              <span className="text-sm text-[#0C2A40] font-medium">{file ? file.name : "Click to upload AR aging file"}</span>
              <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-[#93A5B5]">.xlsx or .csv · max 5MB</span>
            </button>
            <input ref={inputRef} data-testid="audit-file" type="file" accept=".xlsx,.xlsm,.csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />

            <button data-testid="audit-submit" type="submit" disabled={loading} className="w-full py-4 rounded-full bg-[#2880B8] text-white font-mono text-[11px] tracking-[0.15em] uppercase hover:bg-[#1F6EA1] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />} Run My Free Audit
            </button>
          </motion.form>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="audit-result" className="bg-white rounded-2xl border border-[#D8E6F1] p-6 md:p-8">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#93A5B5]">Total AR analyzed · {result.row_count} rows</p>
            <div className="font-mono text-4xl font-bold text-[#0C2A40] mt-1">{money(result.total_ar)}</div>

            <div className="mt-6 space-y-3">
              {buckets.map((b) => (
                <div key={b.l}>
                  <div className="flex justify-between text-xs text-[#51667A] mb-1">
                    <span>{b.l}</span><span className="font-mono">{money(b.v)}</span>
                  </div>
                  <div className="h-2 rounded-full bg-[#E7F1F8] overflow-hidden">
                    <div className={`h-full ${b.danger ? "bg-red-500" : "bg-[#2880B8]"}`} style={{ width: `${(b.v / maxB) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="rounded-xl bg-red-50 p-4">
                <div className="font-mono text-2xl font-bold text-red-600">{result.over_90_pct}%</div>
                <div className="font-mono text-[10px] tracking-[0.12em] uppercase text-red-500/80">AR Over 90 Days</div>
              </div>
              <div className="rounded-xl bg-[#E7F1F8] p-4">
                <div className="flex items-center gap-1 font-mono text-2xl font-bold text-[#2880B8]"><TrendingUp size={18} /> {money(result.estimated_recoverable)}</div>
                <div className="font-mono text-[10px] tracking-[0.12em] uppercase text-[#2880B8]/80">Est. Recoverable</div>
              </div>
            </div>
            <p className="mt-6 text-sm text-[#51667A]">Want Astra to recover this? Our team will follow up at <span className="text-[#0C2A40] font-medium">{result.email}</span>.</p>
          </motion.div>
        )}
      </div>
    </section>
  );
};
